import { useEffect, useMemo, useState, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconCheck, IconChevronDown, IconClose, IconDownload, IconEdit, IconTrash } from '../components/icons';
import { EmployeeLeaveEntitlementsPanel } from '../components/EmployeeLeaveEntitlementsPanel';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import SearchableSelect from '../components/SearchableSelect';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import usePaginatedList from '../hooks/usePaginatedList';

interface Filters {
  search: string;
  status: string;
  dateFrom: string;
  dateTo: string;
}

const emptyFilters: Filters = { search: '', status: '', dateFrom: '', dateTo: '' };

interface LeaveRow {
  id: number;
  user_id: number;
  user_name: string;
  employee_code: string | null;
  type: string;
  start_date: string;
  end_date: string;
  days: number;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected';
  decided_by_name: string | null;
  decision_comment: string | null;
  is_pending: boolean;
  has_attachment: boolean;
  attachment_filename: string | null;
  approval_levels_required: number;
  current_approval_level: number;
  pending_with_name: string | null;
  approvals: ApprovalHistoryEntry[];
}

interface ApprovalHistoryEntry {
  level: number;
  approver_name: string | null;
  decision: string;
  comment: string | null;
  decided_at: string;
}

interface UserOption {
  id: number;
  full_name: string;
  [key: string]: unknown;
}

interface LeaveBalance {
  has_policy: boolean;
  entitled_days: number;
  carried_over_days: number;
  used_days: number;
  remaining_days: number;
}

const STATUS_BADGE: Record<LeaveRow['status'], string> = {
  pending: 'badge-scheduled',
  approved: 'badge-active',
  rejected: 'badge-cancelled',
};

function formatPeriod(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const s = new Date(start).toLocaleDateString('en-GB', opts);
  const e = new Date(end).toLocaleDateString('en-GB', { ...opts, year: 'numeric' });
  return start === end ? e : `${s} – ${e}`;
}

const emptyForm = { user_id: '' as string | number, type: '', start_date: '', end_date: '', reason: '' };

const TABS = ['requests', 'entitlements'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = { requests: 'Requests', entitlements: 'Entitlements' };

export function Leave() {
  const { can } = useAuth();
  const { lists } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();

  const [tab, setTab] = useState<Tab>('requests');
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [addAttachment, setAddAttachment] = useState<File | null>(null);
  const [editing, setEditing] = useState<LeaveRow | null>(null);
  const [editForm, setEditForm] = useState({ type: '', start_date: '', end_date: '', reason: '' });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [balance, setBalance] = useState<LeaveBalance | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const canCreate = can('hr.create');
  const canUpdate = can('hr.update');
  const canApprove = can('hr.approve');
  const canArchive = can('hr.archive');

  const query = useMemo(
    () => ({
      search: filters.search || undefined,
      status: filters.status || undefined,
      date_from: filters.dateFrom || undefined,
      date_to: filters.dateTo || undefined,
    }),
    [filters]
  );

  const { data, loading, error, setPage, reload } = usePaginatedList<LeaveRow>('/leave-requests', query);

  const openAdd = () => {
    setForm(emptyForm);
    setAddAttachment(null);
    setErrors({});
    setBalance(null);
    setAdding(true);
  };

  useEffect(() => {
    if (!adding || !form.user_id || !form.type) {
      setBalance(null);
      return;
    }
    let cancelled = false;
    api
      .get<LeaveBalance>('/leave-requests/balance', { params: { user_id: form.user_id, type: form.type } })
      .then((res) => {
        if (!cancelled) setBalance(res.data);
      })
      .catch(() => {
        if (!cancelled) setBalance(null);
      });
    return () => {
      cancelled = true;
    };
  }, [adding, form.user_id, form.type]);

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('user_id', String(form.user_id));
      data.append('type', form.type);
      data.append('start_date', form.start_date);
      data.append('end_date', form.end_date);
      if (form.reason) data.append('reason', form.reason);
      if (addAttachment) data.append('attachment', addAttachment);
      await api.post('/leave-requests', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAdding(false);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const openEdit = (row: LeaveRow) => {
    setEditing(row);
    setEditForm({ type: row.type, start_date: row.start_date, end_date: row.end_date, reason: row.reason ?? '' });
    setErrors({});
  };

  const submitEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    try {
      await api.put(`/leave-requests/${editing.id}`, editForm);
      setEditing(null);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const decide = async (row: LeaveRow, decision: 'approved' | 'rejected') => {
    const verb = decision === 'approved' ? 'Approve' : 'Reject';
    const confirmed = await confirmAction(
      `${verb} ${row.user_name}'s ${labelFor(lists, 'leave_type', row.type)} request for ${formatPeriod(row.start_date, row.end_date)}?`,
      { confirmLabel: verb }
    );
    if (!confirmed) return;
    try {
      await api.post(`/leave-requests/${row.id}/decide`, { decision });
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : `${verb} failed.`);
    }
  };

  const remove = async (row: LeaveRow) => {
    if (!(await confirmAction(`Delete this leave request for ${row.user_name}?`))) return;
    try {
      await api.delete(`/leave-requests/${row.id}`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const toggleExpanded = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const downloadAttachment = (row: LeaveRow) =>
    downloadFile(api, `/leave-requests/${row.id}/attachment`, row.attachment_filename ?? `${row.user_name}-attachment`);

  const removeAttachment = async (row: LeaveRow) => {
    if (!(await confirmAction(`Remove the attachment for ${row.user_name}?`))) return;
    try {
      await api.delete(`/leave-requests/${row.id}/attachment`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Remove failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Leave</h1>
        {tab === 'requests' && canCreate && (
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            + Add
          </button>
        )}
      </header>

      <div className="toolbar">
        <div className="tab-group">
          {TABS.map((t) => (
            <button key={t} type="button" className={tab === t ? 'tab active' : 'tab'} onClick={() => setTab(t)}>
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      {tab === 'entitlements' && <EmployeeLeaveEntitlementsPanel canManage={canUpdate} />}

      {tab === 'requests' && (
      <>
      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by name…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <label className="checkbox-inline">
          Date from
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
          />
        </label>
        <label className="checkbox-inline">
          Date to
          <input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
        </label>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Employee Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Period</th>
                <th>Status</th>
                <th>Attachment</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.flatMap((row) => {
                  const isExpanded = expanded.has(row.id);
                  const rows = [
                    <tr key={row.id}>
                    <td>
                      <button
                        type="button"
                        className="btn icon-btn btn-ghost"
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        onClick={() => toggleExpanded(row.id)}
                      >
                        <IconChevronDown
                          className="icon-btn-svg"
                          style={{ transform: isExpanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}
                        />
                      </button>
                    </td>
                    <td className="muted">{row.employee_code ?? '—'}</td>
                    <td>
                      <strong>{row.user_name}</strong>
                    </td>
                    <td>{labelFor(lists, 'leave_type', row.type)}</td>
                    <td>
                      {formatPeriod(row.start_date, row.end_date)}
                      <br />
                      <small className="muted">
                        {row.days} day{row.days === 1 ? '' : 's'}
                      </small>
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[row.status]}`}>{row.status}</span>
                      {row.is_pending && (
                        <>
                          <br />
                          <small className="muted">
                            Pending with: {row.pending_with_name ?? 'no manager set'}
                            {row.approval_levels_required > 1 && ` (level ${row.current_approval_level} of ${row.approval_levels_required})`}
                          </small>
                        </>
                      )}
                    </td>
                    <td>
                      {row.has_attachment ? (
                        <>
                          <IconButton
                            icon={IconDownload}
                            label={row.attachment_filename ?? 'Download'}
                            onClick={() => downloadAttachment(row)}
                          />
                          {canUpdate && (
                            <IconButton
                              icon={IconTrash}
                              label="Remove attachment"
                              variant="danger-ghost"
                              onClick={() => removeAttachment(row)}
                            />
                          )}
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="right">
                      {canApprove && row.is_pending && (
                        <>
                          <IconButton icon={IconCheck} label="Approve" onClick={() => decide(row, 'approved')} />
                          <IconButton
                            icon={IconClose}
                            label="Reject"
                            variant="danger-ghost"
                            onClick={() => decide(row, 'rejected')}
                          />
                        </>
                      )}
                      {canUpdate && <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(row)} />}
                      {canArchive && (
                        <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(row)} />
                      )}
                    </td>
                  </tr>,
                  ];

                  if (!isExpanded) return rows;

                  if (row.approvals.length === 0) {
                    rows.push(
                      <tr key={`approvals-empty-${row.id}`}>
                        <td></td>
                        <td colSpan={7} className="muted">
                          No decisions recorded yet.
                        </td>
                      </tr>
                    );
                  } else {
                    row.approvals.forEach((a, i) => {
                      rows.push(
                        <tr key={`approval-${row.id}-${i}`}>
                          <td></td>
                          <td colSpan={3} className="muted">
                            Level {a.level} — {a.approver_name ?? 'Unknown'}
                            {a.comment && <>: {a.comment}</>}
                          </td>
                          <td colSpan={2}>
                            <span className={`badge ${a.decision === 'approved' ? 'badge-active' : 'badge-cancelled'}`}>
                              {a.decision}
                            </span>
                          </td>
                          <td colSpan={2} className="muted">
                            {new Date(a.decided_at).toLocaleString('en-GB')}
                          </td>
                        </tr>
                      );
                    });
                  }

                  return rows;
                })
              ) : (
                <tr>
                  <td colSpan={8} className="muted center">
                    No leave requests yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {adding && (
        <Modal title="Add leave request" onClose={() => setAdding(false)}>
          <form onSubmit={submitAdd} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <SearchableSelect<UserOption>
                endpoint="/users"
                value={form.user_id}
                onChange={(id) => setForm({ ...form, user_id: id })}
                getLabel={(u) => u.full_name}
                placeholder="Search staff…"
              />
              {errors.user_id && <small className="error">{errors.user_id[0]}</small>}
            </label>
            <label className="span-2">
              Type *
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} required>
                <option value="">Select…</option>
                {listOptions(lists, 'leave_type').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.type && <small className="error">{errors.type[0]}</small>}
              {balance?.has_policy && (
                <small className="muted">
                  {balance.remaining_days} of {balance.entitled_days} days remaining this year
                  {balance.carried_over_days > 0 && ` (includes ${balance.carried_over_days} carried over)`}.
                </small>
              )}
            </label>
            <label>
              Start date *
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                required
              />
              {errors.start_date && <small className="error">{errors.start_date[0]}</small>}
            </label>
            <label>
              End date *
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                required
              />
              {errors.end_date && <small className="error">{errors.end_date[0]}</small>}
            </label>
            <label className="span-2">
              Reason
              <textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </label>
            <label className="span-2">
              Attachment
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setAddAttachment(e.target.files?.[0] ?? null)}
              />
              {errors.attachment && <small className="error">{errors.attachment[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!form.user_id}>
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit leave request for ${editing.user_name}`} onClose={() => setEditing(null)}>
          <form onSubmit={submitEdit} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Type *
              <select value={editForm.type} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })} required>
                {listOptions(lists, 'leave_type').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.type && <small className="error">{errors.type[0]}</small>}
            </label>
            <label>
              Start date *
              <input
                type="date"
                value={editForm.start_date}
                onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                required
              />
              {errors.start_date && <small className="error">{errors.start_date[0]}</small>}
            </label>
            <label>
              End date *
              <input
                type="date"
                value={editForm.end_date}
                onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                required
              />
              {errors.end_date && <small className="error">{errors.end_date[0]}</small>}
            </label>
            <label className="span-2">
              Reason
              <textarea
                rows={3}
                value={editForm.reason}
                onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
      </>
      )}
    </>
  );
}
